import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { rgba } from 'polished';

import { withStyles } from '../tools/withStyles';
import { withSounds } from '../tools/withSounds';
import { renderMarkdown } from '../tools/markdown';
import { Link } from '../components/Link';
import { Main } from '../components/Main';
import { Secuence } from '../components/Secuence';
import { Text } from '../components/Text';
import blogPosts from '../data/blogPosts';

const styles = theme => ({
  root: {},
  list: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 14,
    marginTop: 20,
    maxWidth: 880
  },
  reveal: {
    opacity: 0,
    transform: 'translateY(24px)',
    transition: [
      `opacity ${theme.animation.time * 2}ms ease-out`,
      `transform ${theme.animation.time * 2}ms ease-out`
    ].join(',')
  },
  revealVisible: {
    opacity: 1,
    transform: 'translateY(0)'
  },
  card: {
    display: 'block',
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.24),
    padding: 20,
    color: 'inherit',
    backgroundColor: rgba(theme.color.background.dark, 0.28),
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `background-color ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`,
      `transform ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.secondary.main, 0.64),
      backgroundColor: rgba(theme.color.secondary.main, 0.05),
      boxShadow: `0 0 18px ${rgba(theme.color.secondary.main, 0.14)}`,
      transform: 'translateY(-2px)'
    }
  },
  cardMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: 14,
    rowGap: 6,
    margin: [0, 0, 10],
    color: theme.color.secondary.main,
    fontSize: 12,
    lineHeight: 1.4,
    textTransform: 'uppercase'
  },
  cardTitle: {
    margin: [0, 0, 10],
    color: theme.color.primary.main,
    fontSize: 20,
    lineHeight: 1.25
  },
  cardSummary: {
    margin: [0, 0, 14],
    color: theme.color.text.main,
    fontSize: 14,
    lineHeight: 1.55
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },
  tag: {
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.35),
    padding: [4, 12],
    marginRight: 10,
    marginBottom: 8,
    borderRadius: 12,
    color: rgba(theme.color.secondary.main, 0.95),
    fontSize: 11,
    lineHeight: 1.2,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    backgroundColor: rgba(theme.color.secondary.main, 0.08),
    transition: 'border-color 200ms ease, background-color 200ms ease, box-shadow 200ms ease'
  },
  detail: {
    maxWidth: 880,
    outline: 'none'
  },
  detailHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 24
  },
  detailEyebrowRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8
  },
  backIconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    marginRight: 10,
    padding: 0,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.45),
    borderRadius: 4,
    backgroundColor: rgba(theme.color.secondary.main, 0.08),
    color: theme.color.secondary.main,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: `all ${theme.animation.time}ms ease-out`,

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.tertiary.main, 0.8),
      backgroundColor: rgba(theme.color.tertiary.main, 0.2),
      color: theme.color.tertiary.main,
      transform: 'translateX(-2px)'
    }
  },
  backIconSvg: {
    width: 16,
    height: 16,
    display: 'block'
  },
  eyebrow: {
    margin: 0,
    color: theme.color.secondary.main,
    fontSize: 12,
    lineHeight: 1.4,
    textTransform: 'uppercase'
  },
  detailTitle: {
    marginBottom: 0
  },
  detailMeta: {
    margin: [10, 0, 0],
    color: rgba(theme.color.text.main, 0.82),
    fontSize: 13,
    lineHeight: 1.4,
    textTransform: 'uppercase'
  },
  status: {
    margin: [0, 0, 20],
    color: theme.color.secondary.main,
    fontSize: 14,
    lineHeight: 1.5
  },
  markdown: {
    color: theme.color.text.main,
    fontSize: 15,
    lineHeight: 1.75,

    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: [28, 0, 14]
    },
    '& h1:first-child, & h2:first-child, & h3:first-child': {
      marginTop: 0
    },
    '& p': {
      margin: [0, 0, 18]
    },
    '& ul, & ol': {
      margin: [0, 0, 20],
      padding: [0, 0, 0, 22]
    },
    '& li': {
      marginBottom: 8
    },
    '& blockquote': {
      margin: [22, 0],
      borderLeft: '2px solid ' + rgba(theme.color.secondary.main, 0.55),
      padding: [0, 0, 0, 18],
      color: rgba(theme.color.text.main, 0.86)
    },
    '& pre': {
      position: 'relative',
      margin: [22, 0],
      border: '1px solid ' + rgba(theme.color.secondary.dark, 0.28),
      padding: [38, 16, 16],
      color: theme.color.primary.main,
      fontSize: 13,
      lineHeight: 1.6,
      backgroundColor: rgba(theme.color.background.dark, 0.58),
      overflowX: 'auto',
      boxShadow: `0 0 18px ${rgba(theme.color.secondary.main, 0.08)}`
    },
    '& code': {
      border: '1px solid ' + rgba(theme.color.secondary.dark, 0.22),
      padding: [2, 5],
      color: theme.color.tertiary.main,
      fontFamily: 'monospace',
      fontSize: '0.92em',
      backgroundColor: rgba(theme.color.background.dark, 0.48)
    },
    '& pre code': {
      display: 'block',
      border: 'none',
      padding: 0,
      color: theme.color.primary.main,
      backgroundColor: 'transparent'
    },
    '& pre code::before': {
      content: 'attr(data-language)',
      position: 'absolute',
      top: 10,
      right: 12,
      color: rgba(theme.color.secondary.main, 0.68),
      fontFamily: theme.typography.primary,
      fontSize: 10,
      lineHeight: 1,
      textTransform: 'uppercase'
    },
    '& .token-comment': {
      color: rgba(theme.color.text.main, 0.52),
      fontStyle: 'italic'
    },
    '& .token-string': {
      color: theme.color.tertiary.light
    },
    '& .token-keyword': {
      color: theme.color.secondary.light,
      textShadow: `0 0 5px ${rgba(theme.color.secondary.main, 0.28)}`
    },
    '& .token-number': {
      color: theme.color.tertiary.main
    },
    '& .token-function': {
      color: theme.color.primary.light
    },
    '& .token-type': {
      color: theme.color.secondary.main
    },
    '& a': {
      color: theme.color.secondary.main,
      textDecoration: 'underline'
    },
    '& img': {
      margin: [20, 'auto'],
      border: '1px solid ' + rgba(theme.color.secondary.dark, 0.25),
      maxWidth: '100%'
    }
  },

  '@media screen and (min-width: 820px)': {
    detailHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between'
    }
  }
});

const playSound = (sounds, name) => {
  const sound = sounds && sounds[name];

  if (sound) {
    sound.play();
  }
};

const REVEAL_TEXT_DELAY = 140;
const REVEAL_TEXT_DURATION = 420;

class ScrollReveal extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    sounds: PropTypes.object,
    children: PropTypes.func.isRequired
  };

  state = {
    visible: false,
    textVisible: false
  };

  componentDidMount () {
    if (!window.IntersectionObserver) {
      this.show();
      return;
    }

    this.observer = new window.IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.show();
        }
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.16
    });

    this.observer.observe(this.element);
  }

  componentWillUnmount () {
    clearTimeout(this.textTimer);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  show = () => {
    if (this.state.visible) {
      return;
    }

    playSound(this.props.sounds, 'fade');
    this.setState({ visible: true });
    this.textTimer = setTimeout(() => {
      this.setState({ textVisible: true });
    }, REVEAL_TEXT_DELAY);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render () {
    const { classes, children, sounds } = this.props;
    const { visible, textVisible } = this.state;

    return (
      <div
        className={`${classes.reveal} ${visible ? classes.revealVisible : ''}`}
        ref={ref => (this.element = ref)}
        onMouseEnter={() => playSound(sounds, 'hover')}
      >
        {children(textVisible)}
      </div>
    );
  }
}

const TextReveal = ({ children, visible }) => (
  <Text
    stableTime
    typingDuration={REVEAL_TEXT_DURATION}
    animation={{
      independent: true,
      show: visible,
      duration: REVEAL_TEXT_DURATION
    }}
  >
    {children}
  </Text>
);

TextReveal.propTypes = {
  children: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired
};

const BlogCard = ({ classes, post, visible }) => (
  <Link className={classes.card} href={`/blog/${post.slug}`}>
    <span className={classes.cardMeta}>
      <span><TextReveal visible={visible}>{post.date}</TextReveal></span>
    </span>
    <h2 className={classes.cardTitle}>
      <TextReveal visible={visible}>{post.title}</TextReveal>
    </h2>
    <p className={classes.cardSummary}>
      <TextReveal visible={visible}>{post.summary}</TextReveal>
    </p>
    {post.tags && post.tags.length > 0 && (
      <ul className={classes.tags}>
        {post.tags.map(tag => (
          <li className={classes.tag} key={tag}>
            <TextReveal visible={visible}>{tag}</TextReveal>
          </li>
        ))}
      </ul>
    )}
  </Link>
);

BlogCard.propTypes = {
  classes: PropTypes.object.isRequired,
  post: PropTypes.object.isRequired,
  visible: PropTypes.bool.isRequired
};

class Blog extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    sounds: PropTypes.object,
    pageContext: PropTypes.object
  };

  state = {
    markdown: '',
    loading: false,
    failed: false
  };

  componentDidMount () {
    this.loadSelectedPost();
  }

  componentDidUpdate (prevProps) {
    const previousSlug = prevProps.pageContext && prevProps.pageContext.slug;
    const currentSlug = this.getSelectedSlug();

    if (previousSlug !== currentSlug) {
      this.loadSelectedPost();
    }
  }

  componentWillUnmount () {
    this.cancelled = true;
  }

  getSelectedSlug () {
    const { pageContext } = this.props;

    return pageContext && pageContext.slug;
  }

  getSelectedPost () {
    const slug = this.getSelectedSlug();

    return blogPosts.find(post => post.slug === slug);
  }

  loadSelectedPost = () => {
    const post = this.getSelectedPost();

    if (!post || typeof window === 'undefined') {
      this.setState({ markdown: '', loading: false, failed: false });
      return;
    }

    this.cancelled = false;
    this.setState({ markdown: '', loading: true, failed: false });

    window.fetch(post.file)
      .then(response => {
        if (!response.ok) {
          throw new Error('Blog post could not be loaded.');
        }

        return response.text();
      })
      .then(markdown => {
        if (!this.cancelled) {
          this.setState({ markdown, loading: false });
        }
      })
      .catch(() => {
        if (!this.cancelled) {
          this.setState({ loading: false, failed: true });
        }
      });
  }

  renderList () {
    const { classes, sounds } = this.props;

    return (
      <Secuence stagger>
        <header>
          <h1><Text>Blog</Text></h1>
        </header>
        <div className={classes.list}>
          {blogPosts.map(post => (
            <ScrollReveal
              key={post.slug}
              classes={classes}
              sounds={sounds}
            >
              {visible => (
                <BlogCard
                  classes={classes}
                  post={post}
                  visible={visible}
                />
              )}
            </ScrollReveal>
          ))}
        </div>
      </Secuence>
    );
  }

  getCleanMarkdown (content, title) {
    if (!content) return '';
    let cleaned = content.replace(/^---[\s\S]*?---\s*/, '');
    if (title) {
      const escaped = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      cleaned = cleaned.replace(new RegExp(`^#\\s*${escaped}\\s*\\n+`, 'i'), '');
    }
    return cleaned;
  }

  renderDetail (post) {
    const { classes } = this.props;
    const { markdown, loading, failed } = this.state;

    return (
      <article className={classes.detail} tabIndex='-1'>
        <Secuence stagger>
          <header className={classes.detailHeader}>
            <div>
              <div className={classes.detailEyebrowRow}>
                <Link
                  href='/blog'
                  className={classes.backIconBtn}
                  aria-label='Back to blog'
                >
                  <svg
                    className={classes.backIconSvg}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <line x1='19' y1='12' x2='5' y2='12' />
                    <polyline points='12 19 5 12 12 5' />
                  </svg>
                </Link>
                <p className={classes.eyebrow}><Text>Blog Post</Text></p>
              </div>
              <h1 className={classes.detailTitle}><Text>{post.title}</Text></h1>
              <p className={classes.detailMeta}><Text>{post.date}</Text></p>
            </div>
          </header>

          {loading && (
            <p className={classes.status}>
              <Text>Loading Markdown</Text>
            </p>
          )}

          {failed && (
            <p className={classes.status}>
              <Text>Markdown file could not be loaded.</Text>
            </p>
          )}

          {markdown && (
            <div className={classes.markdown}>
              {renderMarkdown(this.getCleanMarkdown(markdown, post.title))}
            </div>
          )}
        </Secuence>
      </article>
    );
  }

  render () {
    const { classes } = this.props;
    const post = this.getSelectedPost();
    const title = post ? `${post.title} - Melih Emik` : 'Blog - Melih Emik';
    const description = post ? post.summary : 'Melih Emik blog posts.';

    return (
      <>
        <Helmet>
          <title>{title}</title>
          <meta name='description' content={description} />
          <meta property='og:title' content={title} />
          <meta property='og:description' content={description} />
          <meta name='twitter:title' content={title} />
          <meta name='twitter:description' content={description} />
        </Helmet>
        <Main className={classes.root}>
          {post ? this.renderDetail(post) : this.renderList()}
        </Main>
      </>
    );
  }
}

export default withStyles(styles)(withSounds()(Blog));
